/**
 * `P0-UIK-011`: the dossier lifecycle as the SCREENS need it , ids, labels,
 * order, and who is being waited on. Section 02, 2.4.1.
 *
 * **Why this package exists, and where its border runs.** 9.1 gives
 * `domain-types` "shared enums and label maps, one vocabulary per concept",
 * and 9.2 sends a stage label and a chip colour here. The border is drawn in
 * that same row of 9.2, and it is the line to remember:
 *
 *   > A stage label, a chip colour, a weight, an expected delay ,
 *   > `packages/domain-types` (labels) and configuration tables (numbers).
 *
 * So `DELAI_ETAPE` and `POIDS_ETAPE` are deliberately **absent**. They are
 * tunable numbers a founder changes without a deployment, and a copy of them
 * compiled into six frontends is a number that drifts the first time one is
 * tuned. Labels are the opposite: they are what the mockups say, they change
 * with the design and not with the business, and a screen inventing its own
 * is the thing this package prevents.
 *
 * **And not `@hakili/contracts`** (round 40's boundary line): contracts holds
 * what the API PROMISES , the error registry, the `RightKey` closure of
 * `DECISION ADDED 06-34`. This package holds what the SCREENS DISPLAY. Two
 * homes, two purposes: a backend refusal code is not a French chip, and the
 * day one of them starts importing the other is the day both stop meaning
 * anything.
 */

/** Who is being waited on. The console NAMES it; the client app never does (2.4.1). */
export const QUI = ["hakili", "client", "expert", "banque", "assureur", "fini"] as const;
export type Qui = (typeof QUI)[number];

/** 2.4.1's `QUI_ETAPE`: the sentence a console shows for the party in the way. */
export const QUI_ETAPE: Readonly<Record<Qui, string>> = {
  hakili: "À vous de jouer",
  client: "En attente du client",
  expert: "En attente de l'expert",
  banque: "En attente de la banque",
  assureur: "En attente de l'assureur",
  fini: "Terminé",
};

export interface EtapeDescriptor {
  readonly id: string;
  /** The full label, as the console prints it. */
  readonly label: string;
  /** The short form, for a chip or a narrow column. */
  readonly court: string;
  readonly qui: Qui;
}

/**
 * The ten mainline stages, IN ORDER. The order is the data: the `Stepper` of
 * 4.6.3 renders the path travelled from it, so a list that sorted itself
 * alphabetically would render a lifecycle nobody lives.
 */
/**
 * `round C3`, ruling 1 on the founder's decision of 10 September 2026:
 * **EXPERT FIRST, THEN PIÈCES**, and this list IS the order.
 *
 * `collecte` stood second and `expertise` third, so a buyer was asked for his
 * payslips before anybody had looked at the car , and every pièce he filed was
 * filed against a vehicle that might fail its check the following Thursday.
 * The two are swapped here, in `dossier.stage_definitions` by migration, and in
 * the stage machine's transitions. **The enum is untouched**: a stage is not
 * renamed by being moved.
 *
 * The order matters here and not only in the database because this array is
 * what every stepper in four applications walks , `cheminStepper` slices it at
 * the current stage and `I20` forbids a future node, so a list in the old order
 * would draw a path nobody travelled.
 */
export const ETAPES_DOSSIER = [
  /**
   * `round C3-A`, `C3-A.2`: **`qui` is the CLIENT's**, by the architect's
   * ruling of 10 September. Both texts kept.
   *
   * *The outgoing descriptor:* « `qui: "hakili"` » , `intake` read as Hakili's
   * own work, the chip saying « En attente, rien à faire ».
   *
   * *The rule in force:* `intake` is the BUYER's stage. The founder's decision
   * of 10 September makes « paiement » nothing but `intake` seen by the buyer
   * with the fee unpaid, and « Lancer les vérifications » his gesture out of
   * it; `WF-CLI-10`'s own trigger is « Régler les frais » ON THE TRACKING CARD.
   * A stage where a man owes money and is told nothing is asked of him is the
   * founder's finding of 11 September wearing different clothes.
   *
   * **The console's panel is unchanged**, and that is the test of the reading:
   * it already offers the closure alone (`round C3`'s `parLeClient`), which is
   * exactly what « Hakili waits for the buyer » looks like from the office.
   */
  { id: "intake", label: "Prise en charge", court: "Prise en charge", qui: "client" },
  { id: "expertise", label: "Vérification du véhicule", court: "Vérification", qui: "expert" },
  { id: "collecte", label: "Collecte des pièces", court: "Collecte", qui: "client" },
  { id: "analyse", label: "Analyse Hakili", court: "Analyse", qui: "hakili" },
  { id: "pret", label: "Prêt à soumettre", court: "Prêt", qui: "hakili" },
  { id: "soumis", label: "Étude bancaire", court: "Étude bancaire", qui: "banque" },
  /**
   * Round `L-2`, R-F (the architect's ruling of 16 September): **`offre` is
   * HAKILI's stage.** 02 §2.4.1 read `client`; the stage is entered BY the
   * buyer's choice (WF-CLI-22) and what follows , the retained bank's
   * accord, the placement of the insurance , is Hakili's to drive, nothing
   * of it the buyer's. `client` printed « En attente du client » on the
   * console the moment he had just chosen. Both texts kept in 02 and 05;
   * the row of `dossier.stage_definitions` moved by migration
   * (`20260917130000_offre_is_hakilis_stage`); the console drawing's
   * `qui: client` at « Offre » flagged for the designer (B11).
   */
  { id: "offre", label: "Offre retenue par le client", court: "Offre retenue", qui: "hakili" },
  { id: "assurance", label: "Placement de l'assurance", court: "Assurance", qui: "assureur" },
  { id: "conditions", label: "Conditions préalables et signature", court: "Conditions", qui: "client" },
  { id: "decaisse", label: "Décaissé, véhicule à récupérer", court: "Décaissé", qui: "fini" },
] as const satisfies readonly EtapeDescriptor[];

export type EtapeMainline = (typeof ETAPES_DOSSIER)[number]["id"];

export interface BrancheTerminaleDescriptor extends EtapeDescriptor {
  /** The mainline stage the file left from. The `Stepper` needs it to draw the path. */
  readonly sortie: EtapeMainline;
  /** The sentence a client is shown. Never a bank's name (2.4.1). */
  readonly explication: string;
}

/**
 * The three terminal branches. They leave the mainline and are **never
 * rendered as future stages** , invariant I20, which `Stepper` owns and which
 * is why `sortie` lives here rather than in a screen.
 */
export const BRANCHES_TERMINALES = [
  {
    id: "refuse",
    label: "Refusé au filtre",
    court: "Refusé au filtre",
    qui: "fini",
    sortie: "analyse",
    explication: "Écarté par le filtre Hakili avant toute soumission : aucune banque n'a reçu ce dossier.",
  },
  {
    id: "refuseBanque",
    label: "Refusé par les banques",
    court: "Refusé",
    qui: "fini",
    sortie: "soumis",
    explication:
      "Toutes les banques saisies ont refusé. C'est le seul cas de refus bancaire que le client apprend, et sans nom de banque.",
  },
  {
    id: "sansSuite",
    label: "Clos sans suite",
    court: "Sans suite",
    qui: "fini",
    sortie: "expertise",
    explication: "Clos sans suite : ni refus ni accord, le dossier s'arrête là avec son motif.",
  },
] as const satisfies readonly BrancheTerminaleDescriptor[];

export type BrancheTerminale = (typeof BRANCHES_TERMINALES)[number]["id"];

/** Every stage a dossier can be in: the ten, plus the three branches. */
export type EtapeDossier = EtapeMainline | BrancheTerminale;

/**
 * `PRET-SEND-FILTER-GUARD` (`P0-DOS-074`): **the stages BEFORE the file is sent to a bank** , the mainline up to `pret`. From `soumis` onward the banks
 * hold the figures the file carried, and nothing may change them under them (the field acts of the reading refuse; the vehicle's price locks).
 * `expertise` is a stage of the mainline order (the vehicle's check) and sits before the send.
 */
export const STAGES_BEFORE_SEND = ["intake", "expertise", "collecte", "analyse", "pret"] as const;
export const isBeforeSend = (stage: string): boolean =>
  (STAGES_BEFORE_SEND as readonly string[]).includes(stage);

/**
 * 2.4.1: `ETAPES_CLOSES`. A file in a closed stage weighs nothing and does not
 * block an offboarding , which is why `P0-IDN-017`'s workload port asks the
 * owning module rather than counting rows itself.
 */
export const ETAPES_CLOSES = ["refuse", "refuseBanque", "sansSuite", "decaisse"] as const;
export type EtapeClose = (typeof ETAPES_CLOSES)[number];

/**
 * `FX-2a`, `M5 (c)`: the closed stages that ended WITHOUT an outcome , `ETAPES_CLOSES`
 * less `decaisse`.
 *
 * **The name is ENGLISH and the stage ids are not**, which is the rule and not an
 * inconsistency: `09-01` freezes French identifiers at 5 September 2026 and every new
 * name is English, while the ids are the catalogue's own words and `ETAPES_CLOSES`
 * predates the freeze. The first draft of this constant was called
 * `ETAPES_CLOSES_SANS_ISSUE` and `scripts`' own canon spec refused it by name , the
 * gate doing exactly what it is for.
 *
 * **Derived and not written out**, which is the whole point of the movement that put
 * it here. `dossier-advance.service.ts` held these three as literals, one short of
 * the four above, and a reader could not tell a deliberate difference from a copy
 * that had fallen behind. It IS deliberate: a disbursed file is closed and
 * SUCCEEDED, and a closure's motive, its origin and its author belong to a file that
 * ended without an outcome. Spelled this way, a fourth branch added to 2.4.1 reaches
 * every caller on its own.
 */
export const CLOSED_WITHOUT_OUTCOME = ETAPES_CLOSES.filter(
  (etape): etape is Exclude<EtapeClose, "decaisse"> => etape !== "decaisse",
);

const BY_ID = new Map<string, EtapeDescriptor>([
  ...ETAPES_DOSSIER.map((etape): [string, EtapeDescriptor] => [etape.id, etape]),
  ...BRANCHES_TERMINALES.map((branche): [string, EtapeDescriptor] => [branche.id, branche]),
]);

/** The descriptor for any stage, mainline or terminal. */
export function etapeDescriptor(etape: EtapeDossier): EtapeDescriptor {
  const found = BY_ID.get(etape);
  if (found === undefined) {
    // Unreachable through the type, and reachable from untyped data at a
    // boundary. Throwing names the value; returning a placeholder would put a
    // stage nobody defined onto a screen.
    throw new Error(`unknown dossier stage: ${etape}`);
  }
  return found;
}

/** The label a screen prints, so no screen ever writes one itself. */
export const etapeLabel = (etape: EtapeDossier): string => etapeDescriptor(etape).label;

/** The short label, for a chip or a narrow column. */
export const etapeCourt = (etape: EtapeDossier): string => etapeDescriptor(etape).court;

/** The sentence naming the party being waited on. */
export const quiLabel = (etape: EtapeDossier): string => QUI_ETAPE[etapeDescriptor(etape).qui];

export const estEtapeClose = (etape: EtapeDossier): boolean =>
  (ETAPES_CLOSES as readonly string[]).includes(etape);

export const estBrancheTerminale = (etape: EtapeDossier): etape is BrancheTerminale =>
  BRANCHES_TERMINALES.some((branche) => branche.id === etape);

/**
 * The path a `Stepper` renders: every mainline stage travelled, and on a
 * terminal branch the branch itself , and **zero future nodes** (I20). The
 * rule lives here because three apps render a stepper and one of them getting
 * it wrong is a client being shown a future that was already refused.
 */
export function cheminStepper(etape: EtapeDossier): readonly EtapeDescriptor[] {
  if (estBrancheTerminale(etape)) {
    const branche = BRANCHES_TERMINALES.find((candidate) => candidate.id === etape);
    if (branche === undefined) throw new Error(`unknown terminal branch: ${etape}`);
    const jusque = ETAPES_DOSSIER.findIndex((candidate) => candidate.id === branche.sortie);
    return [...ETAPES_DOSSIER.slice(0, jusque + 1), branche];
  }
  const index = ETAPES_DOSSIER.findIndex((candidate) => candidate.id === etape);
  if (index < 0) throw new Error(`unknown dossier stage: ${etape}`);
  return ETAPES_DOSSIER.slice(0, index + 1);
}

/**
 * `P1-REST` (the founder's walk, 20 October 2026): **the console's full stepper.** The path travelled and the
 * current stage, then the stages still ahead , so a chargée sees where a live file is going , and on a terminal
 * branch NOTHING ahead (`I20` is about a branch, not about a file still moving). `cheminStepper` is unchanged: the
 * buyer's app and the dealer's keep their paths.
 *
 * On a branch the travelled path ends at the stage the file LEFT: `avant` is the file's stored `stage_before`
 * (stamped on the way into the branch). `sansSuite` can be reached from any open stage, so the branch's own
 * static `sortie` (`expertise`) drew a path nobody travelled for a file closed at analyse; it stays the fallback
 * where no stage is stored, or the stored one is not a main-line stage.
 */
export function fullStepperPath(
  etape: EtapeDossier,
  avant?: string | null,
): { readonly travelled: readonly EtapeDescriptor[]; readonly ahead: readonly EtapeDescriptor[] } {
  if (estBrancheTerminale(etape)) {
    const left = ETAPES_DOSSIER.findIndex((candidate) => candidate.id === avant);
    if (left < 0) return { travelled: cheminStepper(etape), ahead: [] };
    const branche = BRANCHES_TERMINALES.find((candidate) => candidate.id === etape);
    if (branche === undefined) throw new Error(`unknown terminal branch: ${etape}`);
    return { travelled: [...ETAPES_DOSSIER.slice(0, left + 1), branche], ahead: [] };
  }
  const index = ETAPES_DOSSIER.findIndex((candidate) => candidate.id === etape);
  if (index < 0) throw new Error(`unknown dossier stage: ${etape}`);
  return { travelled: ETAPES_DOSSIER.slice(0, index + 1), ahead: ETAPES_DOSSIER.slice(index + 1) };
}

/**
 * `round CW`, `CW.5`: **the chip a BUYER reads, which is not the console's.**
 *
 * 2.4.1's own line , *"Who is being waited on. The console NAMES it; the client
 * app never does"* , and the client app was naming it. `chipAttente` answers
 * `QUI_ETAPE`, whose `hakili` row reads **« À vous de jouer »**, addressed to
 * the chargé looking at his queue. The buyer's screen printed that same
 * sentence, so a file at `intake` , which is Hakili's own work , told the buyer
 * the ball was in HIS court. And `client` answered « En attente du client »,
 * which speaks about a person in the third person on his own file.
 *
 * The drawing has exactly three sentences for the buyer, and they are the ones
 * below: « À vous de jouer » when he must act, « Terminé » when the car is his,
 * and « En attente, rien à faire » for every other party , Hakili, the expert,
 * the bank, the insurer. **A buyer is not told WHO holds his file**, only
 * whether anything is asked of him, which is 2.4.1's rule and also the only
 * distinction he can act on.
 *
 * **The fourth is the record's own exception**, and it is `CHIP_DOSSIER_CLOS`:
 * a terminal branch reads « Dossier clos » and never « Terminé », because a
 * refused file has not succeeded, it has left the path. `stage-machine.ts`
 * implemented that exception alone and said so in its own comment , *"that
 * exception is implemented nowhere in the product ... reported as a gap in the
 * shared vocabulary"*. This is the gap closed: the sentence lives with the
 * other labels, and both the console's chip and the buyer's read it from here.
 */
export const CHIP_DOSSIER_CLOS = "Dossier clos";

/** The four tones a buyer's chip can carry. The tone is the colour's name, never a colour. */
export type TonClient = "vous" | "attente" | "termine" | "clos";

export const CHIP_CLIENT: Readonly<Record<TonClient, string>> = {
  vous: "À vous de jouer",
  attente: "En attente, rien à faire",
  termine: "Terminé",
  clos: CHIP_DOSSIER_CLOS,
};

/**
 * What the buyer's screen shows for a stage: the tone and its sentence.
 *
 * The TONE travels beside the label rather than being re-derived by the screen
 * from the sentence , a screen comparing French strings to choose a colour is
 * one translation away from a grey chip on a finished file.
 */
export function chipClient(etape: EtapeDossier): { readonly ton: TonClient; readonly label: string } {
  const ton: TonClient = estBrancheTerminale(etape)
    ? "clos"
    : etapeDescriptor(etape).qui === "client"
      ? "vous"
      : etapeDescriptor(etape).qui === "fini"
        ? "termine"
        : "attente";
  return { ton, label: CHIP_CLIENT[ton] };
}
